import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, FileText, TrendingUp, Shield, Search, Loader2,
  Gift, Trash2, Plus, Copy, Mail, Database, Activity, CheckCircle, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const [newPromo, setNewPromo] = useState({ code: '', credits: 50, max_uses: 100, description: '' });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [triggeringEmails, setTriggeringEmails] = useState(false);
  const [dbHealth, setDbHealth] = useState(null);
  const [dbHealthLoading, setDbHealthLoading] = useState(false);

  useEffect(() => { fetchAdminData(); }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, promoRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/promo-codes`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      // API returns { users: [...] }, extract the array
      setUsers(usersRes.data.users || usersRes.data || []);
      setPromoCodes(promoRes.data);
    } catch (err) { console.error('Admin fetch failed:', err); }
    finally { setLoading(false); }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setCreatingPromo(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, newPromo, { headers: getAuthHeaders() });
      await fetchAdminData();
      setNewPromo({ code: '', credits: 50, max_uses: 100, description: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create promo code'); }
    finally { setCreatingPromo(false); }
  };

  const handleDeletePromo = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { headers: getAuthHeaders() });
      setPromoCodes(promoCodes.filter(p => p.code !== code));
    } catch (err) { console.error('Failed to delete promo:', err); }
  };

  const handleTriggerChurnEmails = async () => {
    setTriggeringEmails(true);
    try {
      const response = await axios.post(`${API}/admin/trigger-churn-emails`, {}, { headers: getAuthHeaders() });
      alert(`Churn emails sent to ${response.data.emails_sent} users`);
    } catch (err) { alert('Failed to trigger emails'); }
    finally { setTriggeringEmails(false); }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchDbHealth = async () => {
    setDbHealthLoading(true);
    try {
      const res = await axios.get(`${API}/health/db`, { headers: getAuthHeaders() });
      setDbHealth(res.data);
    } catch (err) { console.error('DB health fetch failed:', err); }
    finally { setDbHealthLoading(false); }
  };

  useEffect(() => { if (activeTab === 'system') fetchDbHealth(); }, [activeTab]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'promo', label: 'Promo Codes', icon: Gift },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'system', label: 'System', icon: Database },
  ];

  return (
    <div className="fade-in" data-testid="admin-page">
      <div className="flex items-center gap-2.5 mb-8">
        <Shield className="w-5 h-5 text-[var(--color-coral)]" />
        <div>
          <h1 className="font-serif text-3xl mb-0">Admin Dashboard</h1>
          <p className="text-sm text-[var(--color-muted)]">Founder access</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-8 overflow-x-auto pb-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`btn text-xs ${activeTab === id ? 'btn-primary' : 'btn-secondary'}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Users" value={stats.users?.total || 0} icon={Users} />
          <StatCard label="Total Articles" value={stats.articles?.total || 0} icon={FileText} />
          <StatCard label="Active Subscriptions" value={stats.active_subscriptions || 0} icon={TrendingUp} />
          <StatCard label="Revenue" value={`\u20AC${stats.revenue?.total_eur || 0}`} icon={TrendingUp} />
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted)]" />
            <input type="text" placeholder="Search users..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} className="input pl-10" />
          </div>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[var(--color-surface)]">
                    <th className="text-left p-3 font-medium text-[var(--color-muted)]">User</th>
                    <th className="text-left p-3 font-medium text-[var(--color-muted)]">Plan</th>
                    <th className="text-left p-3 font-medium text-[var(--color-muted)]">Credits</th>
                    <th className="text-left p-3 font-medium text-[var(--color-muted)]">Articles</th>
                    <th className="text-left p-3 font-medium text-[var(--color-muted)]">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id || u.email} className="border-t border-[var(--color-border)]">
                      <td className="p-3">
                        <p className="font-medium">{u.full_name || 'N/A'}</p>
                        <p className="text-[var(--color-muted)]">{u.email}</p>
                      </td>
                      <td className="p-3"><span className="badge badge-draft capitalize">{u.subscription?.tier || 'free'}</span></td>
                      <td className="p-3">{u.subscription?.credits_remaining?.toFixed(1) || '0.0'}</td>
                      <td className="p-3">{u.article_count || 0}</td>
                      <td className="p-3 text-[var(--color-muted)]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Promo Codes Tab */}
      {activeTab === 'promo' && (
        <>
          <form onSubmit={handleCreatePromo} className="card p-6 mb-5">
            <h3 className="font-display text-sm font-medium mb-4">Create Promo Code</h3>
            <div className="grid md:grid-cols-4 gap-3">
              <input type="text" value={newPromo.code} onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                className="input font-mono text-xs" placeholder="CODE" required data-testid="admin-promo-code" />
              <input type="number" value={newPromo.credits} onChange={(e) => setNewPromo({...newPromo, credits: parseInt(e.target.value)})}
                className="input text-xs" placeholder="Credits" required />
              <input type="number" value={newPromo.max_uses} onChange={(e) => setNewPromo({...newPromo, max_uses: parseInt(e.target.value)})}
                className="input text-xs" placeholder="Max uses" required />
              <button type="submit" disabled={creatingPromo} className="btn btn-primary text-xs" data-testid="create-promo-btn">
                {creatingPromo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5" /> Create</>}
              </button>
            </div>
          </form>
          <div className="space-y-2">
            {promoCodes.map((promo) => (
              <div key={promo.code} className="card p-4 flex items-center justify-between" data-testid={`promo-${promo.code}`}>
                <div className="flex items-center gap-4">
                  <code className="text-xs font-mono font-semibold bg-[var(--color-surface)] px-2.5 py-1 rounded-lg">{promo.code}</code>
                  <div>
                    <p className="text-xs">{promo.credits} credits &middot; {promo.used_count || 0}/{promo.max_uses} used</p>
                    {promo.description && <p className="text-[10px] text-[var(--color-muted)]">{promo.description}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigator.clipboard.writeText(promo.code)} className="btn btn-ghost p-2" title="Copy">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDeletePromo(promo.code)} className="btn btn-ghost p-2" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {promoCodes.length === 0 && (
              <p className="text-xs text-[var(--color-muted)] text-center py-8">No promo codes created yet.</p>
            )}
          </div>
        </>
      )}

      {/* Emails Tab */}
      {activeTab === 'emails' && (
        <div className="card p-6">
          <h3 className="font-display text-sm font-medium mb-3">Email Automation</h3>
          <p className="text-xs text-[var(--color-muted)] mb-5">
            Send re-engagement emails to users who haven't been active in 7+ days.
          </p>
          <button onClick={handleTriggerChurnEmails} disabled={triggeringEmails}
            className="btn btn-primary" data-testid="trigger-churn-btn">
            {triggeringEmails ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Trigger Churn Emails</>}
          </button>
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <div className="space-y-5" data-testid="admin-system-tab">
          {dbHealthLoading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="w-5 h-5 animate-spin text-[var(--color-muted)]" /></div>
          ) : dbHealth ? (
            <>
              {/* Status + Ping Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="stat-card" data-testid="system-status-card">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dbHealth.status === 'healthy' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                      {dbHealth.status === 'healthy'
                        ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                        : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                    </div>
                  </div>
                  <div className="stat-value capitalize">{dbHealth.status}</div>
                  <div className="stat-label">Database Status</div>
                </div>
                <div className="stat-card" data-testid="system-ping-card">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
                      <Activity className="w-4 h-4 text-[var(--color-coral)]" />
                    </div>
                  </div>
                  <div className="stat-value">{dbHealth.ping_ms}ms</div>
                  <div className="stat-label">DB Ping Latency</div>
                </div>
                <div className="stat-card" data-testid="system-index-card">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
                      <Database className="w-4 h-4 text-[var(--color-coral)]" />
                    </div>
                  </div>
                  <div className="stat-value">{dbHealth.index_coverage?.percentage}%</div>
                  <div className="stat-label">Index Coverage ({dbHealth.index_coverage?.total_present}/{dbHealth.index_coverage?.total_expected})</div>
                </div>
              </div>

              {/* Index Coverage Detail */}
              <div className="card overflow-hidden">
                <div className="p-4 border-b border-[var(--color-border)]">
                  <h3 className="font-display text-sm font-medium">Index Coverage by Collection</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[var(--color-surface)]">
                        <th className="text-left p-3 font-medium text-[var(--color-muted)]">Collection</th>
                        <th className="text-left p-3 font-medium text-[var(--color-muted)]">Indexes</th>
                        <th className="text-left p-3 font-medium text-[var(--color-muted)]">Status</th>
                        <th className="text-left p-3 font-medium text-[var(--color-muted)]">Missing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbHealth.index_coverage?.collections && Object.entries(dbHealth.index_coverage.collections).map(([col, info]) => (
                        <tr key={col} className="border-t border-[var(--color-border)]">
                          <td className="p-3 font-mono">{col}</td>
                          <td className="p-3">{info.present}/{info.expected}</td>
                          <td className="p-3">
                            {info.present === info.expected
                              ? <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle className="w-3 h-3" /> OK</span>
                              : <span className="inline-flex items-center gap-1 text-amber-500"><AlertTriangle className="w-3 h-3" /> Missing</span>}
                          </td>
                          <td className="p-3 text-[var(--color-muted)]">{info.missing ? info.missing.join(', ') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Document Counts */}
              <div className="card overflow-hidden">
                <div className="p-4 border-b border-[var(--color-border)]">
                  <h3 className="font-display text-sm font-medium">Collection Sizes</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-[var(--color-border)]">
                  {dbHealth.document_counts && Object.entries(dbHealth.document_counts).map(([col, count]) => (
                    <div key={col} className="bg-[var(--color-bg)] p-3.5 text-center" data-testid={`doc-count-${col}`}>
                      <div className="text-lg font-semibold mb-0.5">{count.toLocaleString()}</div>
                      <div className="text-[10px] text-[var(--color-muted)] font-mono truncate">{col}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Refresh */}
              <button onClick={fetchDbHealth} className="btn btn-secondary text-xs" data-testid="refresh-db-health-btn">
                <Activity className="w-3.5 h-3.5" /> Refresh
              </button>
            </>
          ) : (
            <div className="card p-8 text-center">
              <p className="text-xs text-[var(--color-muted)]">Failed to load system status.</p>
              <button onClick={fetchDbHealth} className="btn btn-secondary text-xs mt-3">Retry</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between mb-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
        <Icon className="w-4 h-4 text-[var(--color-coral)]" />
      </div>
    </div>
    <div className="stat-value">{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

export default AdminDashboard;
